import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import * as Icons from "react-feather";
import PageHeader from "../../../../shared/page-header/PageHeader";
import { getApiBaseUrl } from "../../../../utils/apiConfig";
import { getStorageValues } from "../../../../shared/CommonFunctions";
import { exportToExcel } from "../../../../utils/downloadExportedFiles";
import { logAuditEvent } from "../../../../utils/auditLogger";

const EllipsisWithTooltip = ({ text }) => {
  const value = text === null || text === undefined ? "" : String(text);
  return (
    <span
      title={value}
      style={{
        display: "block",
        maxWidth: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        cursor: value ? "help" : "default",
      }}
    >
      {value || "-"}
    </span>
  );
};

function toIsoDateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

async function openApp2PdfInNewTab({ payload }) {
  const res = await axios.post(getApiBaseUrl() + "/reports/app2/pdf-token", payload, {
    headers: { "Content-Type": "application/json" },
  });
  const rid = res?.data?.rid;
  if (!rid) return;
  const pdfUrl = `${getApiBaseUrl()}/reports/app2/pdf?rid=${encodeURIComponent(rid)}`;
  window.open(pdfUrl, "_blank");
}

function columnLabel(name) {
  if (typeof name === "string") return name;
  if (typeof name === "number") return String(name);
  // Handle simple JSX like <div>Title</div>
  try {
    const children = name?.props?.children;
    if (typeof children === "string") return children;
    if (Array.isArray(children)) return children.map((c) => (typeof c === "string" ? c : "")).join("").trim();
  } catch (_) {}
  return "Column";
}

function isProbablyNumericColumn(label) {
  const s = String(label || "").toLowerCase();
  return (
    s.includes("amount") ||
    s.includes("total") ||
    s.includes("qty") ||
    s.includes("quantity") ||
    s.includes("count") ||
    s.includes("sold") ||
    s.includes("units") ||
    s.includes("tat") ||
    s.includes("%") ||
    s.includes("pct") ||
    s.includes("percent") ||
    s.includes("days")
  );
}

/**
 * ReportViewer: shared "standard layout" report page
 *
 * Props:
 * - reportKey: stable id for audit + caching (e.g. "pendingEnquiry")
 * - title: report title
 * - moduleName: module name for audit (defaults to title)
 * - columns: react-data-table-component columns
 * - fetchConfig: { url, method?, paramsBuilder?, transform? }
 * - initialFilters: { fromDate?, toDate?, ...custom }
 * - renderFilters: (filters, setFilters) => JSX
 * - buildKpis: (rows) => [{ label, value }]
 * - pdfGenerator: async ({ title, filters, kpis, columns, rows }) => ({ dataUri, base64 })
 */
export default function ReportViewer({
  reportKey,
  title,
  moduleName,
  columns,
  fetchConfig,
  initialFilters,
  renderFilters,
  buildKpis,
  pdfGenerator,
}) {
  const effectiveModuleName = moduleName || title || "Reports";
  const [filters, setFilters] = useState(() => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      fromDate: toIsoDateInput(firstOfMonth),
      toDate: toIsoDateInput(now),
      ...((initialFilters || {}) ?? {}),
    };
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    logAuditEvent(effectiveModuleName, "Accessed Report", reportKey || title || "Report");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveModuleName]);

  const doFetch = useCallback(async () => {
    if (!fetchConfig?.url) return;
    setLoading(true);
    try {
      const token = getStorageValues("token");
      const method = (fetchConfig.method || "get").toLowerCase();
      const params = typeof fetchConfig.paramsBuilder === "function" ? fetchConfig.paramsBuilder(filters) : filters;

      const req = {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      };
      let resp;
      if (method === "post") {
        resp = await axios.post(getApiBaseUrl() + fetchConfig.url, params, req);
      } else {
        resp = await axios.get(getApiBaseUrl() + fetchConfig.url, { ...req, params });
      }
      const data = resp?.data?.data ?? resp?.data ?? [];
      const normalized = typeof fetchConfig.transform === "function" ? fetchConfig.transform(data, resp) : data;
      setRows(Array.isArray(normalized) ? normalized : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fetchConfig, filters]);

  useEffect(() => {
    doFetch();
  }, [doFetch]);

  const filteredRows = useMemo(() => {
    const term = (searchText || "").trim().toLowerCase();
    if (!term) return rows || [];
    return (rows || []).filter((r) => JSON.stringify(r || {}).toLowerCase().includes(term));
  }, [rows, searchText]);

  const kpis = useMemo(() => {
    if (typeof buildKpis !== "function") return [];
    try {
      const out = buildKpis(filteredRows);
      return Array.isArray(out) ? out : [];
    } catch {
      return [];
    }
  }, [buildKpis, filteredRows]);

  const headerFilters = useMemo(() => {
    const obj = {};
    const f = filters || {};
    const toLabel = (key) => {
      const s = String(key || "");
      if (!s) return "";
      return s
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (m) => m.toUpperCase());
    };

    Object.entries(f).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") return;
      if (Array.isArray(v) && v.length === 0) return;
      // Keep date filters first/consistent
      if (k === "fromDate") return;
      if (k === "toDate") return;
      obj[toLabel(k)] = String(v);
    });
    if (f?.fromDate) obj["From"] = f.fromDate;
    if (f?.toDate) obj["To"] = f.toDate;
    return obj;
  }, [filters]);

  const onExcel = useCallback(async () => {
    await exportToExcel(title || "Report", columns || [], filteredRows || [], { filters: headerFilters });
  }, [title, columns, filteredRows, headerFilters]);

  const onPdf = useCallback(async () => {
    logAuditEvent(effectiveModuleName, "Export PDF", reportKey || title || "Report");
    const userId = getStorageValues("userId");
    const companyId = process.env.REACT_APP_COMPANY_ID || "1";

    const srcCols = columns || [];
    const srcRows = filteredRows || [];

    const pdfColumns = srcCols.map((c, idx) => {
      const name = columnLabel(c?.name);
      let key = "";
      if (typeof c?.selector === "string") key = c.selector;
      if (!key) key = c?.key || c?.pdfKey || "";
      if (!key) key = `col_${idx + 1}`;
      return { name, key, isNumeric: isProbablyNumericColumn(name) };
    });

    const pdfRows = srcRows.map((r) => {
      const out = {};
      pdfColumns.forEach((pc, idx) => {
        const col = srcCols[idx];
        let value = "";
        try {
          if (typeof col?.selector === "function") value = col.selector(r);
          else if (typeof col?.selector === "string") value = r?.[col.selector];
          else if (pc.key) value = r?.[pc.key];
        } catch {
          value = "";
        }
        out[pc.key] = value === null || value === undefined || value === "" ? "-" : String(value);
      });
      return out;
    });

    const isWide = pdfColumns.length > 6 || pdfColumns.some((c) => String(c?.name || "").length > 18);
    const orientation = isWide ? "landscape" : "portrait";

    const payload = {
      title: title || "Report",
      filters: headerFilters,
      kpis,
      columns: pdfColumns,
      rows: pdfRows,
      userId,
      companyId,
      orientation,
    };

    // Open backend URL (refreshable, like loan application PDF).
    await openApp2PdfInNewTab({ payload });
  }, [effectiveModuleName, reportKey, title, headerFilters, kpis, columns, filteredRows]);

  const enhancedColumns = useMemo(() => {
    return (columns || []).map((col) => {
      // If report page already provided a custom cell renderer, keep it.
      if (col?.cell) return col;

      return {
        ...col,
        cell: (row) => {
          let value = "";
          try {
            value = typeof col?.selector === "function" ? col.selector(row) : row?.[col?.selector];
          } catch {
            value = "";
          }
          return <EllipsisWithTooltip text={value} />;
        },
      };
    });
  }, [columns]);

  return (
    <>
      <PageHeader
        displayText={""}
        displayModule={title}
        onRefresh={doFetch}
        showPDF={false}
        showExcel={false}
        showPrint={false}
        showRefresh={true}
        showAddButton={false}
      />

      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top d-flex align-items-center justify-content-between">
            <div className="search-set d-flex align-items-center">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary ms-2"
                onClick={() => setShowFilters((v) => !v)}
                title="Toggle filters"
              >
                <Icons.Filter size={16} className="me-1" />
                Filters
              </button>
            </div>

            <div className="d-flex align-items-center">
              <button className="btn btn-sm btn-outline-success me-2" onClick={onExcel} disabled={!filteredRows?.length}>
                <Icons.Download size={16} className="me-1" />
                Excel
              </button>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={onPdf}
                disabled={!filteredRows?.length || typeof pdfGenerator !== "function"}
                title={typeof pdfGenerator !== "function" ? "PDF export not configured" : "Export PDF"}
              >
                <Icons.FileText size={16} className="me-1" />
                PDF
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="m-3">
              <div className="row g-2 align-items-end">
                <div className="col-sm-12 col-md-3">
                  <label className="form-label">From</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.fromDate || ""}
                    onChange={(e) => setFilters((p) => ({ ...p, fromDate: e.target.value }))}
                  />
                </div>
                <div className="col-sm-12 col-md-3">
                  <label className="form-label">To</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filters.toDate || ""}
                    onChange={(e) => setFilters((p) => ({ ...p, toDate: e.target.value }))}
                  />
                </div>
                {typeof renderFilters === "function" ? renderFilters(filters, setFilters) : null}
              </div>
            </div>
          )}

          {kpis.length > 0 && (
            <div className="row m-2">
              {kpis.map((k) => (
                <div className="col-sm-6 col-md-3" key={k.label}>
                  <div className="dash-count das0">
                    <div className="dash-counts">
                      <h4>
                        <b>{k.value ?? "-"}</b>
                      </h4>
                      <h5>
                        <b>{k.label}</b>
                      </h5>
                    </div>
                    <div className="dash-imgs">
                      <Icons.BarChart2 size={18} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="p-3 pt-0">
            <div className="border rounded">
              <DataTable
                className="table-responsive product-list"
                columns={enhancedColumns || []}
                data={filteredRows || []}
                progressPending={loading}
                persistTableHead
                pagination
                paginationPerPage={20}
                paginationRowsPerPageOptions={[10, 20, 50, 100, 200]}
                highlightOnHover
                responsive
                dense
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

